import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { PencilSimple, Check, X, Plus, Trash, ArrowsClockwise, Warning } from "@phosphor-icons/react";

const AUD = (n) => new Intl.NumberFormat("en-AU", {
    style: "currency", currency: "AUD", maximumFractionDigits: 0,
}).format(n || 0);

const TAB_LABELS = {
    bank_accounts: "Bank Accounts",
    insurance: "Insurance",
    contacts: "Important Contacts",
    documents: "Documents",
    decisions: "Decision Register",
    opportunities: "Opportunity Register",
};

const NUMERIC_FIELDS = new Set(["balance", "annual_premium", "sum_insured", "estimated_cost"]);

function emptyRow(cols) {
    return Object.fromEntries(cols.map(c => [c, NUMERIC_FIELDS.has(c) ? 0 : ""]));
}

function InputCell({ col, value, onChange, testid }) {
    const isNum = NUMERIC_FIELDS.has(col);
    return (
        <input
            type={isNum ? "number" : "text"}
            step="any"
            value={value ?? ""}
            onChange={e => onChange(isNum ? Number(e.target.value) : e.target.value)}
            data-testid={testid}
            className={`border border-[var(--line)] px-2 py-1 rounded text-sm w-full ${isNum ? "text-right font-mono-num" : ""}`}
        />
    );
}

function SyncBankBalances({ onDone }) {
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [toDelete, setToDelete] = useState(new Set());

    const openPreview = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/registers/bank_accounts/sync-preview");
            setPreview(data);
            setToDelete(new Set());
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally {
            setBusy(false);
        }
    };

    const runSync = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(
                "/registers/bank_accounts/sync-to-balance-sheet",
                { delete_legacy_ids: Array.from(toDelete) }
            );
            const msg = `Balance Sheet updated: +${data.created} new, ~${data.updated} refreshed` +
                        (data.deleted_legacy ? `, −${data.deleted_legacy} legacy removed` : "");
            toast.success(msg);
            setPreview(null);
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sync failed");
        } finally {
            setBusy(false);
        }
    };

    const toggleDelete = (id) => setToDelete(s => {
        const n = new Set(s);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });

    return (
        <>
            <button className="btn-primary flex items-center gap-2 text-xs"
                    onClick={openPreview} disabled={busy}
                    data-testid="sync-bank-balances-btn">
                <ArrowsClockwise size={12} /> Sync to Balance Sheet
            </button>

            {preview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                     onClick={() => !busy && setPreview(null)}
                     data-testid="sync-preview-modal">
                    <div className="bg-[var(--bone)] hairline rounded max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto"
                         onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b" style={{ borderColor: "var(--line)" }}>
                            <div className="label-eyebrow mb-1">Bank Balances → Balance Sheet</div>
                            <div className="font-serif text-2xl">Preview the sync</div>
                            <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                                Registers is now the source of truth for cash. Confirm what
                                will change on the Balance Sheet before we write anything.
                            </p>
                        </div>

                        <div className="p-6 space-y-5 text-sm">
                            <div>
                                <div className="label-eyebrow mb-2">New rows to add ({preview.will_insert.length})</div>
                                {preview.will_insert.length === 0 ? (
                                    <div className="text-xs" style={{ color: "var(--muted)" }}>
                                        Nothing new — every register account already has a Balance Sheet row.
                                    </div>
                                ) : preview.will_insert.map(r => (
                                    <div key={r.register_id} className="flex justify-between py-1"
                                         data-testid={`sync-insert-${r.register_id}`}>
                                        <span>{r.entity} · {r.asset}</span>
                                        <span className="font-mono-num">{AUD(r.balance)}</span>
                                    </div>
                                ))}
                            </div>

                            <div>
                                <div className="label-eyebrow mb-2">Rows to refresh ({preview.will_update.length})</div>
                                {preview.will_update.length === 0 ? (
                                    <div className="text-xs" style={{ color: "var(--muted)" }}>
                                        No value changes — register balances already match.
                                    </div>
                                ) : preview.will_update.map(r => (
                                    <div key={r.register_id} className="flex justify-between py-1"
                                         data-testid={`sync-update-${r.register_id}`}>
                                        <span>{r.entity} · {r.asset}</span>
                                        <span className="font-mono-num">→ {AUD(r.balance)}</span>
                                    </div>
                                ))}
                            </div>

                            {preview.legacy_duplicates.length > 0 && (
                                <div className="hairline p-3 rounded"
                                     style={{ background: "#fff7ed", borderColor: "#fdba74" }}>
                                    <div className="flex items-center gap-2 label-eyebrow mb-2"
                                         style={{ color: "#9a3412" }}>
                                        <Warning size={14} weight="fill" /> Possible duplicates already on Balance Sheet
                                    </div>
                                    <div className="text-xs mb-3" style={{ color: "#7c2d12" }}>
                                        Tick any row you want DELETED before the sync (to avoid double-counting).
                                        Leave un-ticked if it's a separate legit asset.
                                    </div>
                                    {preview.legacy_duplicates.map(d => (
                                        <label key={d.id} className="flex items-center gap-3 py-1 cursor-pointer"
                                               data-testid={`sync-dupe-${d.id}`}>
                                            <input type="checkbox"
                                                   checked={toDelete.has(d.id)}
                                                   onChange={() => toggleDelete(d.id)}
                                                   data-testid={`sync-dupe-check-${d.id}`} />
                                            <span className="flex-1">{d.entity} · {d.asset}</span>
                                            <span className="font-mono-num">{AUD(d.value)}</span>
                                        </label>
                                    ))}
                                </div>
                            )}

                            {preview.skipped_smsf.length > 0 && (
                                <div className="text-xs" style={{ color: "var(--muted)" }}>
                                    <div className="label-eyebrow mb-1">Skipped (SMSF)</div>
                                    SMSF cash is tracked via the SMSF Cash Ledger, not Balance Sheet.
                                    These register accounts will not sync:
                                    <ul className="mt-1 ml-4 list-disc">
                                        {preview.skipped_smsf.map((s, i) => (
                                            <li key={i}>{s.institution} · {s.account}
                                                — {AUD(s.balance)}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t flex justify-between items-center"
                             style={{ borderColor: "var(--line)" }}>
                            <button className="btn-ghost text-xs"
                                    onClick={() => setPreview(null)} disabled={busy}
                                    data-testid="sync-cancel-btn">
                                Cancel
                            </button>
                            <button className="btn-primary text-sm flex items-center gap-2"
                                    onClick={runSync} disabled={busy}
                                    data-testid="sync-confirm-btn">
                                <Check size={14} />
                                {busy ? "Syncing…"
                                      : `Confirm sync${toDelete.size ? ` + delete ${toDelete.size}` : ""}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}


function RegisterTable({ name, cols }) {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);        // { id, data }
    const [adding, setAdding] = useState(null);          // data | null
    const [busy, setBusy] = useState(false);

    const load = async () => setRows((await api.get(`/registers/${name}`)).data);
    useEffect(() => { load(); }, [name]);

    const startEdit = (r) => setEditing({ id: r.id, data: { ...r } });
    const cancelEdit = () => setEditing(null);
    const saveEdit = async () => {
        setBusy(true);
        try {
            await api.put(`/registers/${name}/${editing.id}`, editing.data);
            toast.success("Saved");
            setEditing(null);
            await load();
        } catch {
            toast.error("Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const startAdd = () => setAdding(emptyRow(cols));
    const cancelAdd = () => setAdding(null);
    const saveAdd = async () => {
        setBusy(true);
        try {
            await api.post(`/registers/${name}`, adding);
            toast.success("Added");
            setAdding(null);
            await load();
        } catch {
            toast.error("Failed to add");
        } finally {
            setBusy(false);
        }
    };

    const del = async (id) => {
        if (!confirm("Delete this row?")) return;
        await api.delete(`/registers/${name}/${id}`);
        await load();
    };

    return (
        <div>
            <div className="mb-3 flex justify-end gap-2 items-center">
                {name === "bank_accounts" && (
                    <SyncBankBalances onDone={load} />
                )}
                <button className="btn-ghost flex items-center gap-2 text-xs"
                        onClick={startAdd} disabled={adding !== null}
                        data-testid={`register-${name}-add-btn`}>
                    <Plus size={12} /> Add row
                </button>
            </div>
            <div className="hairline bg-white rounded overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="editorial w-full" data-testid={`register-${name}-table`}>
                        <thead>
                            <tr>
                                {cols.map(c => <th key={c}>{c.replace(/_/g, " ")}</th>)}
                                <th>last verified</th>
                                <th style={{ width: 96 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {adding !== null && (
                                <tr className="bg-[var(--eucalyptus)]/10"
                                    data-testid={`register-${name}-add-row`}>
                                    {cols.map(c => (
                                        <td key={c}>
                                            <InputCell
                                                col={c}
                                                value={adding[c]}
                                                onChange={v => setAdding(a => ({ ...a, [c]: v }))}
                                                testid={`register-${name}-add-${c}`}
                                            />
                                        </td>
                                    ))}
                                    <td className="text-[var(--muted)] text-xs">Auto</td>
                                    <td>
                                        <div className="flex items-center gap-1">
                                            <button onClick={saveAdd} disabled={busy}
                                                    className="text-[var(--eucalyptus)] hover:opacity-70"
                                                    data-testid={`register-${name}-add-save`}>
                                                <Check size={16} />
                                            </button>
                                            <button onClick={cancelAdd}
                                                    className="text-[var(--muted)] hover:text-[var(--terracotta)]">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {rows.map(r => {
                                const isEditing = editing?.id === r.id;
                                return (
                                    <tr key={r.id}>
                                        {cols.map(c => (
                                            <td key={c} className="text-sm">
                                                {isEditing ? (
                                                    <InputCell
                                                        col={c}
                                                        value={editing.data[c]}
                                                        onChange={v => setEditing(e => ({ ...e, data: { ...e.data, [c]: v } }))}
                                                        testid={`register-${name}-edit-${r.id}-${c}`}
                                                    />
                                                ) : (
                                                    String(r[c] ?? "—")
                                                )}
                                            </td>
                                        ))}
                                        <td className="text-[var(--muted)] text-xs"
                                            data-testid={`register-${name}-lv-${r.id}`}>
                                            {r.last_verified
                                                ? new Date(r.last_verified).toLocaleDateString("en-AU")
                                                : "—"}
                                        </td>
                                        <td>
                                            {isEditing ? (
                                                <div className="flex items-center gap-1">
                                                    <button onClick={saveEdit} disabled={busy}
                                                            className="text-[var(--eucalyptus)] hover:opacity-70"
                                                            data-testid={`register-${name}-save-${r.id}`}>
                                                        <Check size={16} />
                                                    </button>
                                                    <button onClick={cancelEdit}
                                                            className="text-[var(--muted)] hover:text-[var(--terracotta)]">
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => startEdit(r)}
                                                            className="text-[var(--muted)] hover:text-[var(--ink)]"
                                                            data-testid={`register-${name}-edit-${r.id}`}>
                                                        <PencilSimple size={14} />
                                                    </button>
                                                    <button onClick={() => del(r.id)}
                                                            className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                            data-testid={`register-${name}-del-${r.id}`}>
                                                        <Trash size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {rows.length === 0 && adding === null && (
                    <div className="p-8 text-center text-[var(--muted)] text-sm">
                        No entries yet. Click <strong>Add row</strong> above.
                    </div>
                )}
            </div>
        </div>
    );
}

export default function Registers() {
    const [schema, setSchema] = useState({});
    useEffect(() => {
        api.get("/registers-schema").then(r => setSchema(r.data));
    }, []);

    const keys = Object.keys(schema);
    if (keys.length === 0) return <div className="p-8 text-sm text-[var(--muted)]">Loading…</div>;

    return (
        <div data-testid="registers-page">
            <div className="mb-8">
                <div className="label-eyebrow mb-2">Family Registers</div>
                <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">The paperwork.</h1>
                <p className="text-sm mt-3 max-w-2xl" style={{ color: "var(--muted)" }}>
                    Inline edit any row or add new entries. Bank accounts include BSB and
                    account number fields.
                </p>
            </div>
            <Tabs defaultValue={keys[0]} data-testid="registers-tabs">
                <TabsList className="flex flex-wrap gap-1 bg-transparent p-0 mb-4">
                    {keys.map(k => (
                        <TabsTrigger key={k} value={k} data-testid={`reg-tab-${k}`}
                                     className="data-[state=active]:bg-[var(--eucalyptus)] data-[state=active]:text-[var(--bone)] rounded-md px-4 py-2 text-sm">
                            {TAB_LABELS[k] || k}
                        </TabsTrigger>
                    ))}
                </TabsList>
                {keys.map(k => (
                    <TabsContent key={k} value={k}>
                        <RegisterTable name={k} cols={schema[k]} />
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}
